/**
 * FlyingWebcamClone — fixed-position webcam overlay for the submit fly animation.
 *
 * Ported from mockup-animation-final-v2.html (Apr 22 2026).
 *
 * This component is a forwardRef so PrimateVisionV2 can control its position
 * and visibility directly via DOM style manipulation — exactly like Tom's plain-JS
 * mockup. No React state is used for the fly position; that would introduce
 * render-cycle latency and cause the clone to appear one frame late.
 *
 * Usage:
 *   const flyDivRef = useRef<HTMLDivElement>(null);
 *   <FlyingWebcamClone ref={flyDivRef}>
 *     <video ... /> // optional live feed
 *   </FlyingWebcamClone>
 *
 * The parent shows/hides and positions the clone entirely via flyDivRef.current.style.*
 */

import React, { forwardRef } from 'react';

interface FlyingWebcamCloneProps {
  children?: React.ReactNode;
}

export const FlyingWebcamClone = forwardRef<HTMLDivElement, FlyingWebcamCloneProps>(
  function FlyingWebcamClone({ children }, ref) {
    return (
      <div
        ref={ref}
        aria-hidden="true"
        style={{
          position: 'fixed',
          zIndex: 9999,
          display: 'none',        /* hidden until fly fires — set via DOM ref */
          overflow: 'hidden',
          pointerEvents: 'none',
          background: '#000',
        }}
      >
        {/* Corner brackets — matches mockup .cb elements */}
        {(['tl','tr','bl','br'] as const).map(pos => (
          <div key={pos} style={{
            position: 'absolute', width: 10, height: 10,
            borderColor: 'rgba(27,94,255,0.32)', borderStyle: 'solid', zIndex: 3,
            ...(pos === 'tl' ? { top: 5, left: 5,   borderWidth: '1.5px 0 0 1.5px' } : {}),
            ...(pos === 'tr' ? { top: 5, right: 5,  borderWidth: '1.5px 1.5px 0 0' } : {}),
            ...(pos === 'bl' ? { bottom: 5, left: 5, borderWidth: '0 0 1.5px 1.5px' } : {}),
            ...(pos === 'br' ? { bottom: 5, right: 5, borderWidth: '0 1.5px 1.5px 0' } : {}),
          }} />
        ))}

        {/* WEBCAM label — matches mockup .wtag */}
        <div style={{
          position: 'absolute', top: 7, left: 7,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 7, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.28)', background: 'rgba(0,0,0,0.45)',
          padding: '2px 6px', borderRadius: 3, zIndex: 5,
        }}>
          WEBCAM · 16:9
        </div>

        {/* Live dot — matches mockup .wlive */}
        <div style={{
          position: 'absolute', top: 10, right: 7,
          width: 5, height: 5, borderRadius: '50%',
          background: '#4ADE80', zIndex: 5,
          animation: 'pv2-blink 1.5s ease-in-out infinite',
        }} />

        {/* Webcam video content */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
          {children}
        </div>
      </div>
    );
  }
);
